/**
 * anonIndex.ts
 * Maintains a local index of anonymous entities created by this user/device.
 * 
 * This index is used for:
 * - Exporting all anonymous keys at once
 * - Showing user which entities they have edit tokens for
 * 
 * The index contains NO sensitive data - just entity types and IDs.
 * Tokens are stored separately in the encrypted vault.
 */

import { storage } from "./storage";
import type { AnonEntity } from "./anonTokenVault";

/**
 * Reference to an anonymous entity
 */
export type KeyRef = {
  type: AnonEntity;
  id: number | string;
  createdAt?: string; // Optional timestamp for sorting
};

const KEY = "anonIndex:v1";

/**
 * Add a reference to an anonymous entity
 * Called after creating an anonymous entity
 */
export function addAnonRef(ref: KeyRef): void {
  const current = storage.getJSON<KeyRef[]>(KEY, []);
  
  // Check if already exists
  const exists = current.some(
    (e) => e.type === ref.type && String(e.id) === String(ref.id)
  );
  
  if (!exists) {
    // Add timestamp if not provided
    const entry: KeyRef = {
      ...ref,
      createdAt: ref.createdAt || new Date().toISOString(),
    };
    
    // Add to beginning (newest first)
    storage.setJSON(KEY, [entry, ...current]);
  }
}

/**
 * Remove a reference to an anonymous entity
 * Called when user claims ownership (anonymous → public)
 */
export function removeAnonRef(ref: KeyRef): void {
  const current = storage.getJSON<KeyRef[]>(KEY, []);
  const filtered = current.filter(
    (e) => !(e.type === ref.type && String(e.id) === String(ref.id))
  );
  storage.setJSON(KEY, filtered);
}

/**
 * List all anonymous entity references
 * Returns newest first
 */
export function listAnonRefs(): KeyRef[] {
  return storage.getJSON<KeyRef[]>(KEY, []);
}

/**
 * Clear all anonymous entity references
 * Note: This does NOT delete the tokens from the vault
 */
export function clearAnonRefs(): void {
  storage.remove(KEY);
}

/**
 * Get count of anonymous entities
 */
export function getAnonRefCount(): number {
  return listAnonRefs().length;
}

